import {
    ReactNode,
    createContext,
    useCallback,
    useLayoutEffect,
    useRef,
} from 'react';

import useGoogleFonts from '../../hooks/fetching/useGoogleFonts';
import Theme from '../../types/UI/Theme';

import defaultTheme from './defaultTheme';
import borderRadiusToPixels from './helpers/borderRadiusToPixels';

import styles from './ThemeProvider.module.css';

export const ThemeContext = createContext<Theme>(defaultTheme);

type ThemeProviderProps = {
    theme?: Theme;
    scopeToLocal?: boolean;
    children: ReactNode;
};

const dummyContext = {
    setProperty: () => {},
};

const ThemeProvider = ({
    theme,
    children,
    scopeToLocal = false,
}: ThemeProviderProps) => {
    const themeOrFallback = theme ?? defaultTheme;

    const ref = useRef<HTMLDivElement>(null);
    const resolveContext = useCallback(() => {
        if (!scopeToLocal) {
            return document.documentElement.style;
        }

        if (!ref.current) {
            console.warn('[LB] Could not resolve style context');
            return dummyContext;
        }

        return ref.current.style;
    }, [scopeToLocal]);

    useGoogleFonts([themeOrFallback.fonts.body, themeOrFallback.fonts.title]);

    useLayoutEffect(() => {
        const styleContext = resolveContext();
        styleContext.setProperty(
            '--font-default',
            `${themeOrFallback.fonts.body}, sans-serif`
        );
        styleContext.setProperty(
            '--font-titles',
            `${themeOrFallback.fonts.title}, sans-serif`
        );

        /**
         * Override the default boldness for Rubik
         */
        styleContext.setProperty(
            '--font-weight-bold',
            themeOrFallback.fonts.body === 'Rubik' ? '500' : '700'
        );
    }, [
        themeOrFallback.fonts.body,
        themeOrFallback.fonts.title,
        resolveContext,
    ]);

    useLayoutEffect(() => {
        const styleContext = resolveContext();
        const neutralTints = themeOrFallback.colors.neutral.tints;
        styleContext.setProperty('--color-neutral-100', neutralTints['100']);
        styleContext.setProperty('--color-neutral-200', neutralTints['200']);
        styleContext.setProperty('--color-neutral-300', neutralTints['300']);
        styleContext.setProperty('--color-neutral-400', neutralTints['400']);
        styleContext.setProperty('--color-neutral-500', neutralTints['500']);
        styleContext.setProperty('--color-neutral-600', neutralTints['600']);
        styleContext.setProperty('--color-neutral-700', neutralTints['700']);
        styleContext.setProperty('--color-neutral-800', neutralTints['800']);

        const neutralText = themeOrFallback.colors.neutral.text;
        styleContext.setProperty('--color-neutral-text', neutralText.default);
        styleContext.setProperty(
            '--color-neutral-text-subdued',
            neutralText.subdued
        );

        const brandColors = themeOrFallback.colors.brand;
        styleContext.setProperty(
            '--color-brand-default',
            brandColors.default.tint
        );
        styleContext.setProperty(
            '--color-brand-default-contrast',
            brandColors.default.contrast
        );
        styleContext.setProperty(
            '--color-brand-darker',
            brandColors.darker.tint
        );
        styleContext.setProperty(
            '--color-brand-darker-contrast',
            brandColors.darker.contrast
        );
        styleContext.setProperty(
            '--color-brand-lighter',
            brandColors.lighter.tint
        );
        styleContext.setProperty(
            '--color-brand-lighter-contrast',
            brandColors.lighter.contrast
        );
        styleContext.setProperty(
            '--color-brand-lighterer',
            brandColors.lighterer.tint
        );
        styleContext.setProperty(
            '--color-brand-lighterer-contrast',
            brandColors.lighterer.contrast
        );
    }, [themeOrFallback.colors, resolveContext]);

    useLayoutEffect(() => {
        const ctx = resolveContext();
        const {
            default: defaultRadius,
            large,
            maybePill,
        } = borderRadiusToPixels[themeOrFallback.borderRadius];

        ctx.setProperty('--border-radius-default', `${defaultRadius}px`);
        ctx.setProperty('--border-radius-large', `${large}px`);
        ctx.setProperty('--border-radius-maybe-pill', `${maybePill}px`);
    }, [themeOrFallback.borderRadius, resolveContext]);

    return (
        <ThemeContext.Provider value={themeOrFallback}>
            <div className={styles.ThemeProvider} ref={ref}>
                {children}
            </div>
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
